import { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, X, Rss } from "lucide-react";

export default function IDGRssTab() {
  const [feeds, setFeeds] = useState([]);
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Form states
  const [form, setForm] = useState({ feed_url: "", target_board_id: "", author_id: "system" });
  const [showForm, setShowForm] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [feedsRes, boardsRes] = await Promise.all([
        api.get("/idg/admin/rss-feeds"),
        api.get("/boards")
      ]);
      setFeeds(feedsRes.data || []);
      setBoards(boardsRes.data || []);
    } catch (e) {
      toast.error("Eroare la încărcarea datelor: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleDelete = async (feedId) => {
    if (!window.confirm("Sigur dorești să ștergi acest feed RSS?")) return;
    try {
      await api.delete(`/idg/admin/rss-feeds/${feedId}`);
      toast.success("Feed RSS șters!");
      loadData();
    } catch (e) {
      toast.error("Eroare la ștergere: " + (e?.response?.data?.detail || e.message));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.feed_url.trim()) return toast.error("URL-ul feed-ului este obligatoriu");
    if (!form.target_board_id) return toast.error("Alege un board destinație");
    
    try {
      await api.post("/idg/admin/rss-feeds", form);
      toast.success("Feed RSS configurat cu succes!");
      setShowForm(false);
      setForm({ feed_url: "", target_board_id: "", author_id: "system" });
      loadData();
    } catch (e) {
      toast.error("Eroare la salvare: " + (e?.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="space-y-4" data-testid="rss-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="tf-heading text-2xl font-bold uppercase tracking-wider flex items-center gap-2">
            <Rss className="w-6 h-6 text-[var(--tf-primary)]" /> Auto-RSS Importer
          </h2>
          <p className="text-xs text-zinc-500 tf-mono">Importator automat de știri externe prin fluxuri RSS/Atom direct în board-uri predefinite</p>
        </div>
        {!showForm && (
          <button onClick={() => setShowForm(true)} className="tf-btn-primary tf-clip-btn text-xs flex items-center gap-1">
            <Plus className="w-3.5 h-3.5" /> Adaugă Feed
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="tf-surface tf-clip-card p-5 space-y-4 border border-white/5">
          <div className="flex items-center justify-between border-b border-white/5 pb-2">
            <h3 className="text-sm font-bold text-white uppercase">Adaugă Flux RSS Nou</h3>
            <button type="button" onClick={() => setShowForm(false)} className="text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="tf-section-title block mb-1">URL Flux RSS (ex: https://news.google.com/rss...)</label>
              <input 
                type="url" 
                value={form.feed_url} 
                onChange={(e) => setForm({ ...form, feed_url: e.target.value })} 
                required 
                className="tf-input"
                placeholder="https://feedpress.me/rss-feed-url"
              />
            </div>

            <div>
              <label className="tf-section-title block mb-1">Board destinație</label>
              <select 
                value={form.target_board_id} 
                onChange={(e) => setForm({ ...form, target_board_id: e.target.value })} 
                required
                className="tf-input"
              >
                <option value="">Alege boardul...</option>
                {boards.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="tf-section-title block mb-1">Autor Topic (id sau "system")</label>
              <input 
                type="text" 
                value={form.author_id} 
                onChange={(e) => setForm({ ...form, author_id: e.target.value })} 
                className="tf-input"
                placeholder="system"
              />
            </div>
          </div>

          <div className="flex gap-2 pt-2 border-t border-white/5">
            <button type="submit" className="tf-btn-primary tf-clip-btn text-xs flex items-center gap-1">
              <Save className="w-3.5 h-3.5" /> Salvează
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="tf-btn-secondary tf-clip-btn text-xs">Anulează</button>
          </div>
        </form>
      )}

      <div className="tf-surface tf-clip-card overflow-hidden">
        <table className="w-full text-left text-sm text-zinc-300 leading-normal">
          <thead>
            <tr className="bg-white/5 text-zinc-400 text-xs font-bold uppercase tracking-wider border-b border-white/5">
              <th className="px-5 py-3">URL Feed RSS</th>
              <th className="px-5 py-3">Board Destinație</th>
              <th className="px-5 py-3">ID Autor</th>
              <th className="px-5 py-3">Ultimul Guid Procesat</th>
              <th className="px-5 py-3 text-right">Șterge</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {feeds.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-5 py-10 text-center text-zinc-500 italic">Nu există importuri RSS configurate.</td>
              </tr>
            ) : (
              feeds.map((f) => {
                const targetBoard = boards.find(b => b.id === f.target_board_id);
                return (
                  <tr key={f.id} className="hover:bg-white/5 transition-colors">
                    <td className="px-5 py-3 font-mono text-xs max-w-xs truncate text-white" title={f.feed_url}>{f.feed_url}</td>
                    <td className="px-5 py-3">{targetBoard ? targetBoard.name : <span className="text-zinc-600 italic">—</span>}</td>
                    <td className="px-5 py-3 font-mono text-xs">{f.author_id}</td>
                    <td className="px-5 py-3 max-w-xs truncate font-mono text-zinc-500 text-xs">{f.last_checked_guid || <span className="italic">never run</span>}</td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => handleDelete(f.id)} className="p-1 hover:text-red-500" title="Șterge"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
